import { ActiveOrderTypeOptions, ORDER_STATE_OPTIONS, SYMBOL_TYPE_OPTIONS } from '@features/admin/trading/tradingAdminProtos';
import { WidgetProps } from '@features/dashboard/types';
import { Filter, FilterType } from '@protos/filter';
import { TradingOrdersGrid } from '@shared/components/TradingOrdersGrid/TradingOrdersGrid';
import { useDashboardContext } from '@shared/contexts/DashboardContext';
import { useTradableProductSymbolsContext } from '@shared/contexts/TradableProductSymbolsProvider';
import { useUserContext } from '@shared/contexts/UserContextProvider';
import { ColumnVisibleEvent } from 'ag-grid-enterprise';

import { checkIsABNUser } from '@utils/permissions';
import { debounce } from 'lodash';
import { useMemo, useState } from 'react';
import Settings from '../settings/Settings';
import { useWidgetLifecycleTelemetry } from '../useWidgetLifecycleTelemetry';
import Widget, { Content, Header } from '../Widget';
import { TradingBlotterWidgetPayload } from './tradingBlotterTypes';
import { useAppStore } from '@features/common/store/AppStore';
import { useOrderDetailsPanel } from '@shared/components/OrderDetailsPanel/useOrderDetailsPanel';
import { createOrderDetailsSidebarConfig, createOrderPanelDetails } from '@shared/components/OrderDetailsPanel/orderDetailsUtils';

type TradingBlotterProps = WidgetProps & {
  payload?: TradingBlotterWidgetPayload;
  isFrozen?: boolean;
};

export const TradingBlotter = ({ payload, isFrozen, ...otherProps }: TradingBlotterProps) => {
  const { apiClient } = useAppStore();
  const { id, title } = otherProps;
  const { editWidgetPayloadById } = useDashboardContext();
  const [hiddenColumns, setHiddenColumns] = useState<string[]>(payload?.hiddenColumns ?? []);
  const user = useUserContext();
  const products = useTradableProductSymbolsContext();
  const debouncedEditWidgetPayloadById = debounce(editWidgetPayloadById, 500);
  const limitedToAccounts = checkIsABNUser(user) ? user?.abn_trad_accounts : user?.trading_accounts;
  useWidgetLifecycleTelemetry(id, title, 'TradingBlotter', payload);

  const {
    selectedRow,
    visiblePanel,
    onGridApiAvailable,
    onRowClicked,
    onCellKeyDown,
    onRowDoubleClicked,
    onRowHighlighted,
    onToolPanelVisibilityChanged,
  } = useOrderDetailsPanel({
    apiClient: apiClient!,
    enableRowDataUpdateTracking: true,
  });

  const filters: Filter[] = useMemo(
    () => [
      { type: FilterType.AUTOCOMPLETE, name: 'symbol_type', label: 'Symbol Type', options: SYMBOL_TYPE_OPTIONS },
      { type: FilterType.AUTOCOMPLETE, name: 'order_state', label: 'Order State', options: ORDER_STATE_OPTIONS },
      { type: FilterType.AUTOCOMPLETE, name: 'product_symbol', label: 'Product Symbol', options: products.allSymbols },
      { type: FilterType.DATETIMERANGE, name: 'date_range', label: 'Date Range' },
      { type: FilterType.AUTOCOMPLETE, name: 'order_type', label: 'Type', options: ActiveOrderTypeOptions },
    ],
    [products]
  );

  const onColumnVisible = (event: ColumnVisibleEvent) => {
    const { column, visible } = event;
    const columnId = column?.getColId();

    if (!columnId) return;

    const newHidden = visible ? hiddenColumns.filter(id => id !== columnId) : [...hiddenColumns, columnId];

    setHiddenColumns(newHidden);
    debouncedEditWidgetPayloadById(id, { hiddenColumns: newHidden });
  };

  const orderPanelDetails = createOrderPanelDetails(selectedRow);
  const sideBarConfig = createOrderDetailsSidebarConfig(orderPanelDetails, visiblePanel, { includeColumns: true, includeDetails: true });

  return (
    <Widget {...otherProps}>
      <Header title={title}>
        <Settings id={`trading-settings-${id}`} title={title} widgetId={id} />
      </Header>
      <Content>
        <TradingOrdersGrid
          domLayout="normal"
          isAdmin={false}
          filters={filters}
          sideBar={sideBarConfig}
          onColumnVisible={onColumnVisible}
          hiddenColumns={hiddenColumns}
          limitedToAccounts={limitedToAccounts}
          isFrozen={isFrozen}
          onRowClicked={onRowClicked}
          onRowDoubleClicked={onRowDoubleClicked}
          onCellKeyDown={onCellKeyDown}
          onRowHighlighted={onRowHighlighted}
          onApiAvailable={onGridApiAvailable}
          onToolPanelVisibilityChanged={onToolPanelVisibilityChanged}
        />
      </Content>
    </Widget>
  );
};
